import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, Server, Activity, Lock, RefreshCw, DollarSign, Globe, HardDrive } from "lucide-react";
import ArchitectureDiagram from "@/components/marketing/ArchitectureDiagram";
import PricingCalculator from "@/components/marketing/PricingCalculator";
import ComparisonTable from "@/components/marketing/ComparisonTable";
import { Eyebrow } from "@/components/shared";

const benefits = [
  [ShieldCheck, "No hardware", "No second server, no disks to buy, monitor or replace."],
  [Globe, "No second site", "Skip colocation and offsite facilities entirely."],
  [Zap, "Fast setup", "A managed PBS instance, live in minutes."],
  [Server, "Native Proxmox workflow", "Works exactly like adding any PBS storage."],
  [Lock, "Offsite protection", "Your backups live safely away from your infrastructure."],
  [DollarSign, "Predictable pricing", "One flat monthly price. Scale when you need to."],
];

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 zk-grid-bg opacity-40" />
        <div className="absolute inset-0 zk-radial" />
        <div className="relative mx-auto max-w-7xl px-4 pt-20 pb-16 sm:px-6 lg:px-8 lg:pt-28">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="zk-fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900/60 px-3 py-1 text-xs text-slate-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 zk-pulse" /> Compatible with Proxmox VE & PBS 4.x
              </div>
              <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
                Offsite Proxmox backups <span className="text-cyan-400">in minutes.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
                Launch a secure managed Proxmox Backup Server in the cloud without buying another server, building a second site, or becoming a cloud storage expert.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/signup" data-testid="hero-primary-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-400 px-6 py-3 font-semibold text-[#06080D] transition-all hover:bg-cyan-300 hover:shadow-[0_0_28px_-4px_rgba(0,229,255,0.6)]">
                  Deploy My Backup Server <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/how-it-works" data-testid="hero-secondary-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-700 px-6 py-3 font-semibold text-slate-200 transition-colors hover:border-slate-500">
                  See How It Works
                </Link>
              </div>
              <p className="mt-5 text-xs text-slate-600">Your Proxmox. Our cloud. Your backups protected.</p>
            </div>
            <div className="zk-fade-up" style={{ animationDelay: "0.15s" }}>
              <ArchitectureDiagram />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <Eyebrow>Why ZettaKeep</Eyebrow>
        <h2 className="max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">Offsite disaster recovery, without the datacenter.</h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map(([Icon, title, desc]) => (
            <div key={title} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 transition-colors hover:border-cyan-500/30">
              <Icon className="h-6 w-6 text-cyan-400" />
              <div className="mt-4 font-display font-semibold text-slate-100">{title}</div>
              <p className="mt-1.5 text-sm text-slate-400">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <Eyebrow>The choice</Eyebrow>
        <h2 className="mb-3 font-display text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">Traditional offsite PBS vs ZettaKeep</h2>
        <p className="mb-8 max-w-2xl text-slate-400">You're not choosing between two SaaS tools — you're choosing between building an offsite backup site yourself, or clicking Deploy.</p>
        <ComparisonTable />
      </section>

      {/* Calculator */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <Eyebrow>Simple, predictable pricing</Eyebrow>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">Pay for allocated storage. Nothing surprising.</h2>
            <p className="mt-4 text-slate-400">We bill on your reserved storage plan — not your logical backup size. Deduplication and compression savings are yours to keep. Move the slider to size your plan.</p>
            <div className="mt-6 flex items-center gap-3">
              <Activity className="h-5 w-5 text-emerald-400" />
              <span className="text-sm text-slate-300">Verified backups, monitored health, automatic pruning included.</span>
            </div>
          </div>
          <PricingCalculator />
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-slate-900 to-[#06080D] p-10 text-center sm:p-16">
          <div className="absolute inset-0 zk-radial" />
          <div className="relative">
            <HardDrive className="mx-auto h-10 w-10 text-cyan-400" />
            <h2 className="mt-5 font-display text-3xl font-bold text-slate-50 sm:text-4xl">Your offsite backup server is one click away.</h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-400">Create an account, pick your storage and region, and deploy a fully managed Proxmox Backup Server.</p>
            <Link to="/signup" className="mt-8 inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-8 py-3.5 font-semibold text-[#06080D] transition-all hover:bg-cyan-300">
              Start Backing Up <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
